"use client";

import { c as _c } from "react/compiler-runtime";
import { Button } from "@workspace/ui/components/button";
import { cn } from "@workspace/ui/lib/utils";
import { X } from "lucide-react";
import { createContext, type ReactNode, use, useMemo, useState } from "react";
import { MagnusHostProvider, useMagnusHost } from "./host";
type PanelState = {
  content: ReactNode;
  title?: string;
} | null;
interface FloatingSidePanelContextValue {
  close: () => void;
  panel: PanelState;
}
const FloatingSidePanelContext = createContext<FloatingSidePanelContextValue | null>(null);

/**
 * Scopes the Magnus `sidePanel` host capability to the floating widget.
 *
 * Tool views (Confluence pages, file previews, node details) call
 * `host.sidePanel.open(...)`. In the host app that mounts content into a
 * page-level split; inside the small floating/sidebar widget there's no room
 * for that, so this provider overrides `sidePanel` to mount the content as an
 * overlay *inside* the panel (see {@link FloatingChatSidePanel}). All other
 * host capabilities pass through unchanged.
 *
 * Wrap the {@link FloatingChatBody} contents with this and render a
 * {@link FloatingChatSidePanel} as a sibling of the chat.
 */
export function FloatingChatSidePanelProvider(t0) {
  const $ = _c(13);
  const {
    children
  } = t0;
  const parent = useMagnusHost();
  const [panel, setPanel] = useState(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      open: (content, title) => setPanel({
        content,
        title
      }),
      close: () => setPanel(null),
      setTitle: title_0 => setPanel(prev => prev ? {
        ...prev,
        title: title_0 ?? undefined
      } : prev)
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const sidePanel = t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => setPanel(null);
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== panel) {
    t3 = {
      panel,
      close: t2
    };
    $[2] = panel;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const value = t3;
  let t4;
  if ($[4] !== children || $[5] !== parent.data || $[6] !== parent.media || $[7] !== parent.navigation || $[8] !== parent.persistence) {
    t4 = <MagnusHostProvider data={parent.data} media={parent.media} navigation={parent.navigation} persistence={parent.persistence} sidePanel={sidePanel}>{children}</MagnusHostProvider>;
    $[4] = children;
    $[5] = parent.data;
    $[6] = parent.media;
    $[7] = parent.navigation;
    $[8] = parent.persistence;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t4 || $[11] !== value) {
    t5 = <FloatingSidePanelContext value={value}>{t4}</FloatingSidePanelContext>;
    $[10] = t4;
    $[11] = value;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  return t5;
}

/**
 * The overlay surface that tool-opened content mounts into inside the floating
 * widget. Renders nothing until a tool opens the side panel. Place as a sibling
 * of the chat within {@link FloatingChatBody}; requires a
 * {@link FloatingChatSidePanelProvider} ancestor.
 */
export function FloatingChatSidePanel(t0) {
  const $ = _c(16);
  const {
    className
  } = t0;
  const ctx = use(FloatingSidePanelContext);
  if (!ctx?.panel) {
    return null;
  }
  let t1;
  if ($[0] !== className) {
    t1 = cn("absolute inset-0 z-30 flex animate-in flex-col border-t border-border bg-background fade-in", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = ctx.panel.title ?? "Panel";
  let t3;
  if ($[2] !== t2) {
    t3 = <span className="mr-auto truncate font-medium text-sm">{t2}</span>;
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <X className="size-4" />;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== ctx.close) {
    t5 = <Button aria-label="Close panel" className="size-7" onClick={ctx.close} size="icon" type="button" variant="ghost">{t4}</Button>;
    $[5] = ctx.close;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t3 || $[8] !== t5) {
    t6 = <div className="flex shrink-0 items-center gap-2 border-b px-3 py-2">{t3}{t5}</div>;
    $[7] = t3;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== ctx.panel.content) {
    t7 = <div className="min-h-0 flex-1 overflow-auto">{ctx.panel.content}</div>;
    $[10] = ctx.panel.content;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== t1 || $[13] !== t6 || $[14] !== t7) {
    t8 = <div className={t1}>{t6}{t7}</div>;
    $[12] = t1;
    $[13] = t6;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  return t8;
}